import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePostRoles } from '@dhl/keycloak-user-api'
import { Box, Button, Card, Checkbox, Grid, List, ListItem, ListItemSelect } from '@dhl-ui/components'
import { useCurrentUser, useNotificationMessage } from '~/common'
import { useKeyCloakRoles } from '~/modules/adminusers/hooks'
import { convertBusinessUnitToRole, convertToBusinessUnits, type RoleName } from '~/modules/adminusers/model'

type Props = {
  userId: string
  userBusinessUnits: string[]
  userRoles: RoleName[]
  onBack: () => void
  onRefetch: () => void
}

export const BusinessUnitMappingsCard = ({ userId, userBusinessUnits, userRoles, onBack, onRefetch }: Props) => {
  const { t } = useTranslation()
  const { businessUnits } = useKeyCloakRoles()
  const { mutateAsync: postUserRoles } = usePostRoles()

  const { showError, showSuccess } = useNotificationMessage()
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<string[]>([])

  const { roles: currentUserRoles } = useCurrentUser()

  const currentUserBusiness = useMemo(() => {
    return Array.isArray(currentUserRoles) ? convertToBusinessUnits(currentUserRoles as RoleName[]) : []
  }, [currentUserRoles])

  useEffect(() => {
    if (userBusinessUnits) {
      // When user business units are loaded from the api, set them as default for the selection
      setSelectedBusinessUnits(userBusinessUnits)
    }
  }, [userBusinessUnits])

  const handleSelectBusinessUnit = (select: boolean | undefined, name: string) => () => {
    const newSelection = select
      ? selectedBusinessUnits?.concat(name)
      : selectedBusinessUnits.filter((bu) => bu !== name)
    setSelectedBusinessUnits(newSelection)
  }

  const handleSave = async () => {
    const newKeyCloakBusinessUnits = selectedBusinessUnits.map(convertBusinessUnitToRole)
    // In keycloak business units are stored as roles
    const newKeyCloakRoles = newKeyCloakBusinessUnits.concat(userRoles)
    await postUserRoles(
      {
        p1: userId,
        data: newKeyCloakRoles,
      },
      {
        onSuccess: () => {
          showSuccess(t('admin.adminUsersPage.businessUnitsPage.updateBU'))
        },
        onError: ({ status }) => {
          if (status === 401) {
            showError(t('admin.permissions.notAllowed'))
          } else {
            showError(t('admin.adminUsersPage.businessUnitsPage.errorMessage'))
          }
        },
      }
    )
    onRefetch()
    onBack()
  }

  return (
    <Card data-testid="businessUnit-form">
      <Card.Header title={t('admin.adminUsersPage.businessUnitsPage.titleText')} />
      <Card.Content>
        <List>
          {businessUnits.map((businessUnit: string) => {
            const userHasBusinessUnit = selectedBusinessUnits.some((bu) => businessUnit === bu)
            const userCanEditBusinessUnit = currentUserBusiness.includes(businessUnit)
            return (
              <ListItem
                hasDivider={false}
                key={`businessUnit-${businessUnit}`}
                data-testid={`businessUnit-${businessUnit}`}
              >
                <ListItemSelect>
                  <Checkbox
                    edge="start"
                    checked={userHasBusinessUnit}
                    name="mappedBusinessUnitCodes"
                    label={t(`admin.adminUsersPage.businessUnitsPage.${businessUnit}`)}
                    disabled={!userCanEditBusinessUnit}
                    onChange={handleSelectBusinessUnit(!userHasBusinessUnit, businessUnit)}
                  />
                </ListItemSelect>
              </ListItem>
            )
          })}
        </List>
        <Grid>
          <Box marginTop={2} display="flex" justifyContent="flex-end">
            <Box mr={1}>
              <Button data-testid="click-cancel" size="medium" variant="outlined" color="primary" onClick={onBack}>
                {t('admin.form.cancel')}
              </Button>
            </Box>
            <Box marginRight={2}>
              <Button data-testid="click-save" size="medium" variant="contained" color="primary" onClick={handleSave}>
                {t('admin.adminUsersPage.userForm.saveButtonTitle')}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Card.Content>
    </Card>
  )
}
